import { CategoryIcon } from 'components/icons/category-icon'
import { LibraryIcon } from 'components/icons/library-icon'
import { RasaPageComponentProps } from 'components/rasa-page-component/types'
import { SearchBarComponent } from 'components/search-bar'
import { SearchTypeComponent } from 'components/search-type'
import React, { useCallback, useEffect, useState } from 'react'
import {
  CONTENT_LIBRARY_PAGE,
  FILTER_BY_CATEGORY_TEXT,
  GAEvents,
  PAGE_HEADER_DESCRIPTION,
  Pages,
  SEARCH_PAGE,
} from '../../rootConstants'
import { sendGAEvent } from '../utils'
import './_styles.scss'

type SearchPageComponentProps = RasaPageComponentProps

export const SearchPageComponent: React.FC<SearchPageComponentProps> = ({ store, navigate }) => {
  const [recentSearches, setRecentSearches] = useState<string[]>([])

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch {
        // Ignore invalid JSON
      }
    }
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Focus search on '/' key
      if (e.key === '/' && e.target !== document.querySelector('input')) {
        e.preventDefault()
        const searchInput = document.querySelector('.rct-search-bar input') as HTMLInputElement
        if (searchInput) {
          searchInput.focus()
        }
      }
      // Hide suggestions on Escape
      if (e.key === 'Escape') {
        // Escape key handling removed
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const saveSearchTerm = useCallback(
    (term: string) => {
      if (term.trim() && !recentSearches.includes(term)) {
        const updated = [term, ...recentSearches.slice(0, 4)] // Keep only 5 recent searches
        setRecentSearches(updated)
        localStorage.setItem('recentSearches', JSON.stringify(updated))
      }
    },
    [recentSearches]
  )

  const searchSubmit = useCallback(
    (term: string) => {
      saveSearchTerm(term)
      store.dispatch(
        (x => ({ type: CONTENT_LIBRARY_PAGE, payload: x }))({
          searchTerm: term,
          senderPage: SEARCH_PAGE,
        })
      )
      sendGAEvent(store.getState(), { action: GAEvents.Page1Search, label: term })
      navigate(Pages.contentLibrary)
    },
    [store, navigate, saveSearchTerm]
  )

  const searchByCategory = useCallback(() => {
    sendGAEvent(store.getState(), { action: GAEvents.Page1Categories })
    navigate(Pages.selectCategory)
  }, [store, navigate])

  const handleRecentSearchClick = useCallback(
    (term: string) => {
      searchSubmit(term)
    },
    [searchSubmit]
  )

  return (
    <div className="modern-search-page">
      <div className="modern-search-page__container">
        {/* Header Section */}
        <header className="modern-search-page__header">
          <div className="modern-search-page__icon">
            <LibraryIcon />
          </div>
          <h1 className="modern-search-page__title">Content Library</h1>
          <p className="modern-search-page__description">{PAGE_HEADER_DESCRIPTION}</p>
        </header>

        {/* Search Section */}
        <div className="modern-search-page__search-section">
          <div className="modern-search-page__search-container">
            <SearchBarComponent onSubmit={searchSubmit} />
            <div className="modern-search-page__search-help">
              <span className="modern-search-page__keyboard-hint">
                Press <kbd>/</kbd> to focus search
              </span>
            </div>
          </div>

          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div className="modern-search-page__recent-searches">
              <h3 className="modern-search-page__recent-title">Recent searches</h3>
              <div className="modern-search-page__recent-list">
                {recentSearches.map((term, index) => (
                  <button
                    key={index}
                    className="modern-search-page__recent-item"
                    onClick={() => handleRecentSearchClick(term)}
                    type="button"
                  >
                    <span className="modern-search-page__recent-icon">🕒</span>
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="modern-search-page__divider">
          <span className="modern-search-page__divider-text">or</span>
        </div>

        {/* Browse by Category */}
        <div className="modern-search-page__category-section">
          <SearchTypeComponent icon={<CategoryIcon />} title={FILTER_BY_CATEGORY_TEXT} onSubmit={searchByCategory} />
        </div>

        {/* Quick Start Tips */}
        <div className="modern-search-page__tips">
          <div className="modern-search-page__tip">
            💡 <strong>Tip:</strong> Use specific keywords like "tech news" or "marketing blogs" for better results
          </div>
        </div>
      </div>
    </div>
  )
}
